import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { makeDirectory, type Directory, type User } from '@gdec/shared';
import { api, errorMessage } from '@/api/client';

export interface DirectoryState {
  /** Everyone, active and inactive — the admin screens show both. */
  users: User[];
  directory: Directory;
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
}

/**
 * The employee directory behind every administration screen.
 *
 * Inactive people are included because cycle detection and the "reports to"
 * pickers have to reason about the same set of records the server does — a
 * partial directory produces a *different* answer, not an error.
 */
export function useDirectory(): DirectoryState {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const alive = useRef(true);

  const reload = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      const list = await api.admin.listUsers<User[]>({ includeInactive: true });
      if (!alive.current) return;
      setUsers(list);
      setError(null);
    } catch (err) {
      if (alive.current) setError(errorMessage(err));
    } finally {
      if (alive.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    alive.current = true;
    void reload();
    return () => {
      alive.current = false;
    };
  }, [reload]);

  const directory = useMemo(() => makeDirectory(users), [users]);

  return { users, directory, loading, error, reload };
}
